import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Input, Select, InputNumber, Switch, message } from 'antd';
import { routeApi, channelApi, type RouteDTO, type ChannelDTO } from '../api/kfc';
import { COUNTRY_LIST, formatCountryLabel } from '../constants/countries';

const RouteManage: React.FC = () => {
  const [list, setList] = useState<RouteDTO[]>([]);
  const [channels, setChannels] = useState<ChannelDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form] = Form.useForm();

  const load = () => {
    setLoading(true);
    routeApi.list()
      .then((res) => setList(res.data))
      .catch(() => { setList([]); message.warning('无法加载线路列表'); })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    channelApi.list().then((res) => setChannels(res.data)).catch(() => setChannels([]));
  }, []);

  const openAdd = () => { setEditingId(null); form.resetFields(); setModalOpen(true); };
  const openEdit = (r: RouteDTO) => {
    setEditingId(r.id);
    form.setFieldsValue({
      routeCode: r.routeCode,
      originCountryCode: r.origin?.countryCode,
      originCityName: r.origin?.cityName,
      destinationCountryCode: r.destination?.countryCode,
      destinationCityName: r.destination?.cityName,
      transitTime: r.transitTime,
      transportMode: r.transportMode,
      priceCoefficient: r.priceCoefficient,
      channelIds: r.channelIds ?? [],
      isActive: r.isActive !== false,
    });
    setModalOpen(true);
  };

  const onFinish = (values: Record<string, unknown>) => {
    const originCountry = COUNTRY_LIST.find((c) => c.code === values.originCountryCode);
    const destCountry = COUNTRY_LIST.find((c) => c.code === values.destinationCountryCode);
    const payload: Partial<RouteDTO> = {
      routeCode: values.routeCode as string,
      origin: {
        countryName: originCountry?.nameEn,
        countryCode: values.originCountryCode as string,
        cityName: values.originCityName as string,
      },
      destination: {
        countryName: destCountry?.nameEn,
        countryCode: values.destinationCountryCode as string,
        cityName: values.destinationCityName as string,
      },
      transitTime: values.transitTime as string,
      transportMode: values.transportMode as string,
      priceCoefficient: values.priceCoefficient as number,
      channelIds: values.channelIds as string[],
      isActive: values.isActive !== false,
    };
    const submit = editingId ? routeApi.update(editingId, payload) : routeApi.create(payload);
    submit.then(() => {
      message.success(editingId ? '更新成功' : '创建成功');
      setModalOpen(false);
      load();
    }).catch((e) => message.error(e.response?.data?.message || '操作失败'));
  };

  return (
    <>
      <Card title="线路列表" extra={<Button type="primary" onClick={openAdd}>新增线路</Button>}>
        <Table
          loading={loading}
          dataSource={list}
          rowKey="id"
          columns={[
            { title: '线路编码', dataIndex: 'routeCode', width: 140 },
            {
              title: '起运地',
              width: 160,
              render: (_: unknown, r: RouteDTO) =>
                r.origin
                  ? `${r.origin.countryCode ?? ''} ${r.origin.cityName ?? ''}`.trim() || '-'
                  : '-',
            },
            {
              title: '目的地',
              width: 160,
              render: (_: unknown, r: RouteDTO) =>
                r.destination
                  ? `${r.destination.countryCode ?? ''} ${r.destination.cityName ?? ''}`.trim() || '-'
                  : '-',
            },
            { title: '时效', dataIndex: 'transitTime', width: 80 },
            { title: '运输方式', dataIndex: 'transportMode', width: 90 },
            { title: '价格系数', dataIndex: 'priceCoefficient', width: 90 },
            { title: '启用', dataIndex: 'isActive', width: 60, render: (v: boolean) => (v ? '是' : '否') },
            { title: '操作', width: 80, render: (_, r: RouteDTO) => <Button type="link" size="small" onClick={() => openEdit(r)}>编辑</Button> },
          ]}
        />
      </Card>
      <Modal title={editingId ? '编辑线路' : '新增线路'} open={modalOpen} onCancel={() => setModalOpen(false)} footer={null} width={520}>
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item name="routeCode" label="线路编码" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="originCountryCode" label="起运地-国家" rules={[{ required: true, message: '请选择起运地国家' }]}>
            <Select
              placeholder="请选择国家（编码 英文/中文）"
              showSearch
              optionFilterProp="label"
              options={COUNTRY_LIST.map((c) => ({ label: formatCountryLabel(c), value: c.code }))}
            />
          </Form.Item>
          <Form.Item name="originCityName" label="起运地-城市" rules={[{ required: true, message: '请输入起运地城市' }]}>
            <Input placeholder="请输入城市名称" />
          </Form.Item>
          <Form.Item name="destinationCountryCode" label="目的地-国家" rules={[{ required: true, message: '请选择目的地国家' }]}>
            <Select
              placeholder="请选择国家（编码 英文/中文）"
              showSearch
              optionFilterProp="label"
              options={COUNTRY_LIST.map((c) => ({ label: formatCountryLabel(c), value: c.code }))}
            />
          </Form.Item>
          <Form.Item name="destinationCityName" label="目的地-城市" rules={[{ required: true, message: '请输入目的地城市' }]}>
            <Input placeholder="请输入城市名称" />
          </Form.Item>
          <Form.Item name="transitTime" label="时效">
            <Input placeholder="如 5-7工作日" />
          </Form.Item>
          <Form.Item name="transportMode" label="运输方式">
            <Select options={[{ label: '空运', value: 'AIR' }, { label: '海运', value: 'SEA' }, { label: '快递', value: 'EXPRESS' }]} />
          </Form.Item>
          <Form.Item name="priceCoefficient" label="价格系数" initialValue={1}>
            <InputNumber min={0.01} step={0.1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="channelIds" label="选择渠道（从渠道列表）">
            <Select mode="multiple" placeholder="选择渠道" options={channels.map((c) => ({ label: `${c.channelCode} (${c.carrier})`, value: c.id }))} />
          </Form.Item>
          <Form.Item name="isActive" label="启用" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">保存</Button>
            <Button style={{ marginLeft: 8 }} onClick={() => setModalOpen(false)}>取消</Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default RouteManage;
